import { XNGICONS, XNGIconRenderer } from "../../icons";
import SchoolIcon from "@mui/icons-material/School";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { getSizing } from "../../sizing";
import Box from "../../components-dev/BoxExtended";
import {
  SidebarItemAnyProps as SidebarItemAnyProps,
  ButtonSidebarItemProps as ButtonSidebarItemProps,
  DropdownSidebarItemProps as DropdownSidebarItemProps,
  HeaderSidebarItemProps as HeaderSidebarItemProps,
  SubheaderSidebarItemProps as SubheaderSidebarItemProps,
} from "../../types/sidebar_content";
import { useXNGDispatch, useXNGSelector } from "../../../context/store";
import { setSidebarOpen, sidebarOpen } from "../../../context/slices/sidebarSlice";
import SquareIconButton from "./icon_button_square";
import { SIZE, useSidebarPalette } from "./_";
import ButtonSidebarItem from "./sidebar_item_button";
import DropdownSidebarItem from "./sidebar_item_dropdown";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { MSBICONS } from "../../../fortitude";
import SidebarAccordion from "../../../views/admin/common/sidebar_accordion";
import useSidebarLayoutTabs from "../../../views/admin/constants/sidebar_layout_btns";
import { ROUTES_XLOGS } from "../../../constants/URLs";
import { selectStateInUS } from "../../../context/slices/stateInUsSlice";
import useUserRoles from "../../../hooks/use_user_roles";
import { readSidebarSnapshot, writeSidebarSnapshot } from "./sidebar_persistence";

interface IXNGSidebar {
  sidebarContent: SidebarItemAnyProps[];
  view?: "admin" | "student";
  /**
   * Saves the open state and the content groups' and subheaders' expansion in this browser
   * under this key, and restores them on load. With a key, closing the sidebar hides the
   * groups without forgetting them and reopening restores them; without one, closing
   * collapses every group and reopening expands every group.
   */
  persistKey?: string;
}
function XNGSidebar(props: IXNGSidebar) {
  // HOOKS
  const palette = useSidebarPalette();
  const location = useLocation();
  const dispatch = useXNGDispatch();
  const { toolsButtons, unassignedButtons, studentManagementButtons, caseloadManagementButtons, calendarManagementButtons, billingButtons } =
    useSidebarLayoutTabs();
  const userRoles = useUserRoles()
  const isDeligatedOrExecutiveAdmin = useMemo(()=>userRoles.includes("Executive Admin") || userRoles.includes("Delegated Admin"),[userRoles]) 
  const isCaseLoadManagementActive = useXNGSelector(
    (state) => state.featureFlags.flags["CaseloadManagementActive"],
  );
  // CONSTANTS
  const EXPANDED_WIDTH = getSizing(36);

  // REDUX STATE
  const isSidebarOpen = useXNGSelector(sidebarOpen);
  const state = useXNGSelector(selectStateInUS);

  // TYPE CHECKERS
  function sbiIsDropdown(sbi: SidebarItemAnyProps): sbi is DropdownSidebarItemProps {
    return "tabs" in sbi;
  }
  function sbiIsHeader(sbi: SidebarItemAnyProps): sbi is HeaderSidebarItemProps {
    return "header" in sbi;
  }

  // Group the flat content by its header markers: items before the first
  // header render flat above the groups; each header starts a collapsible
  // group whose children are the items (and subheader labels) that follow it.
  const { leadingItems, groups } = useMemo(() => {
    const leadingItems: SidebarItemAnyProps[] = [];
    const groups: {
      header: HeaderSidebarItemProps;
      children: (ButtonSidebarItemProps | SubheaderSidebarItemProps)[];
    }[] = [];
    for (const sbi of props.sidebarContent) {
      if (sbiIsHeader(sbi)) groups.push({ header: sbi, children: [] });
      else if (groups.length === 0) leadingItems.push(sbi);
      else
        groups[groups.length - 1].children.push(
          sbi as ButtonSidebarItemProps | SubheaderSidebarItemProps,
        );
    }
    return { leadingItems, groups };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.sidebarContent]);

  // STATES
  const { persistKey } = props;
  const [saved] = useState(() => (persistKey ? readSidebarSnapshot(persistKey) : null));
  const [open, setOpen] = useState<number | null>(null);
  const [expandedAccordions, setExpandedAccordions] = useState<String[]>(
    () =>
      saved?.groups ?? [
        // Student Management group is expanded by default when landing on any of its pages (ticket §12)
        ...(location.pathname.includes(ROUTES_XLOGS._admin.studentManagement) ||
        location.pathname.includes(ROUTES_XLOGS._admin.unassignedStudents)
          ? ["Student Management"]
          : []),
        // Content-defined groups start expanded.
        ...groups.map((g) => g.header.header),
      ],
  );

  // Restore the saved open state before the first paint, so the sidebar does not visibly
  // collapse and reopen on load.
  useLayoutEffect(() => {
    if (saved?.open !== undefined) dispatch(setSidebarOpen(saved.open));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (persistKey) writeSidebarSnapshot(persistKey, { groups: expandedAccordions.map(String) });
  }, [persistKey, expandedAccordions]);

  // CALLBACKS
  function onSidebarClose() {
    // close all dropdowns, if any
    setOpen(null);
    if (persistKey) {
      writeSidebarSnapshot(persistKey, { open: false });
      return;
    }
    setExpandedAccordions([]);
  }

  function onSidebarOpen() {
    if (persistKey) {
      writeSidebarSnapshot(persistKey, { open: true });
      setOpen(1);
      return;
    }
    setExpandedAccordions([
      "User Management",
      "Student Management",
      "Calendar Management",
      "Caseload Management",
      "Tools",
      "Billing",
      ...groups.map((g) => g.header.header),
    ]);
    setOpen(1);
  }

  function onAccordionClick(description: string) {
    dispatch(setSidebarOpen(true));
    if (persistKey) writeSidebarSnapshot(persistKey, { open: true });
    setOpen(1);
    if (expandedAccordions.includes(description)) {
      setExpandedAccordions(expandedAccordions.filter((item) => item !== description));
    } else {
      setExpandedAccordions([...expandedAccordions, description]);
    }
  }

  // DOM HIERARCHY
  return (
    <Box
      sx={{
        width: isSidebarOpen ? EXPANDED_WIDTH : getSizing(7),
        bgcolor: palette.bgcolor,
        height: "100%",
        padding: getSizing(1),
        overflow: "hidden",
        transition: "width .2s ease",
      }}
    >
      <Box width={EXPANDED_WIDTH}>
        <MainHamburger onSidebarClose={() => onSidebarClose()} onSidebarOpen={onSidebarOpen} />
        {props.view === "admin" ? (
          <>
            {state !== "NH" ? (
              <ButtonSidebarItem
                displayAsSelected={location.pathname === ROUTES_XLOGS._admin.mydistrictProfile}
                item={{
                  icon: <MSBICONS.Home />,
                  label: "My District Profile",
                  route: ROUTES_XLOGS._admin.mydistrictProfile,
                }}
              />
            ) : null}
            <SidebarAccordion
              children={props.sidebarContent as ButtonSidebarItemProps[]}
              onClick={onAccordionClick}
              description="User Management"
              expanded={!!open && expandedAccordions.includes("User Management")}
              icon={<MSBICONS.AvatarWithCircleOutline />}
            />
            <SidebarAccordion
              children={calendarManagementButtons}
              onClick={onAccordionClick}
              description="Calendar Management"
              expanded={!!open && expandedAccordions.includes("Calendar Management")}
              icon={<MSBICONS.Calendar />}
            />
           {isDeligatedOrExecutiveAdmin && isCaseLoadManagementActive && <SidebarAccordion
              children={caseloadManagementButtons}
              onClick={onAccordionClick}
              description="Caseload Management"
              expanded={!!open && expandedAccordions.includes("Caseload Management")}
              icon={<MSBICONS.Hierarchy />}
            />}
            <SidebarAccordion
              children={studentManagementButtons}
              onClick={onAccordionClick}
              description="Student Management"
              expanded={!!open && expandedAccordions.includes("Student Management")}
              icon={<SchoolIcon fontSize="small" />}
            />
            <SidebarAccordion
              children={toolsButtons}
              onClick={onAccordionClick}
              description="Tools"
              expanded={!!open && expandedAccordions.includes("Tools")}
              icon={<MSBICONS.Tools />}
            />
            {billingButtons.length > 0 && (
              <SidebarAccordion
                children={billingButtons}
                onClick={onAccordionClick}
                description="Billing"
                expanded={!!open && expandedAccordions.includes("Billing")}
                icon={<AttachMoneyIcon fontSize="small" />}
              />
            )}
            {unassignedButtons.map((sbi: ButtonSidebarItemProps, i: number) => (
              <ButtonSidebarItem
                displayAsSelected={location.pathname === sbi.route}
                item={sbi}
                key={i}
              />
            ))}
          </>
        ) : (
          <>
            {leadingItems.map((sbi: SidebarItemAnyProps, i: number) => {
              if (sbiIsDropdown(sbi)) {
                return (
                  <DropdownSidebarItem
                    displayAsSelected={location.pathname === sbi.route}
                    item={sbi as DropdownSidebarItemProps}
                    isOpen={i === open}
                    onSetOpen={() => {
                      if (!isSidebarOpen) {
                        setOpen(i);
                        dispatch(setSidebarOpen(true));
                        return;
                      }
                      if (i === open) {
                        setOpen(null);
                      } else {
                        setOpen(i);
                      }
                    }}
                    key={i}
                  />
                );
              }
              return (
                <ButtonSidebarItem
                  displayAsSelected={location.pathname === (sbi as ButtonSidebarItemProps).route}
                  item={sbi as ButtonSidebarItemProps}
                  key={i}
                />
              );
            })}
            {groups.map((group) => (
              <SidebarAccordion
                key={group.header.header}
                children={group.children}
                onClick={onAccordionClick}
                description={group.header.header}
                expanded={isSidebarOpen && expandedAccordions.includes(group.header.header)}
                icon={group.header.icon ?? <></>}
                persistKey={persistKey}
              />
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}

function MainHamburger(props: { onSidebarClose: () => void; onSidebarOpen: () => void }) {
  const dispatch = useXNGDispatch();
  const isSidebarOpen = useXNGSelector(sidebarOpen);
  const palette = useSidebarPalette();

  return (
    <SquareIconButton
      onClick={() => {
        if (isSidebarOpen) {
          props.onSidebarClose();
          dispatch(setSidebarOpen(false));
        } else {
          props.onSidebarOpen();
          dispatch(setSidebarOpen(true));
        }
      }}
      sx={{ marginBottom: getSizing(3) }}
    >
      <XNGIconRenderer color={palette.deselected} size={SIZE} i={<XNGICONS.Hamburger />} />
    </SquareIconButton>
  );
}

export default XNGSidebar;
